/**
 * The authenticated frame, 1:1 with the K3 shell: the rail from 1024, the top bar (⌘K, "Yaratish",
 * the bell, the avatar), and under 640 a bottom bar of the role's four sections and "Menyu". The
 * page title belongs to the page (`ui/page-header`), not to this bar.
 */
import { Menu, Plus, Search, SquareCheckBig, SquareKanban, Users, Wallet } from "lucide-react";
import { lazy, Suspense, useEffect, useState, type JSX } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router";

import { useRealtimeSync } from "@/app/useRealtimeSync";
import { useApplyPreferences } from "@/features/profile/preferences";
import { useTranslation, type TranslationKey } from "@/i18n";
import { cn } from "@/lib/cn";
import { canEditCrmRecords, canViewKassaBalances } from "@/lib/permissions";
import { showsSuspendedScreen } from "@/lib/support";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/ui/button";
import { DropdownMenu, type MenuItem } from "@/ui/dropdown-menu";

import { hidesTabBar, isNavActive, mobileTabs } from "./navigation";
import { NotificationBell } from "./NotificationBell";
import { PushPrompt } from "./PushPrompt";
import { SearchPalette } from "./SearchPalette";
import { ShellAlerts } from "./ShellAlerts";
import { Drawer, Rail } from "./Sidebar";
import { SupportBanner } from "./SupportBanner";
import { SuspendedScreen } from "./SuspendedScreen";
import { useNavCounts } from "./useNavCounts";
import { UserMenu } from "./UserMenu";
import { locksViewport } from "./viewportLock";

// Loaded with the first paint, but outside the entry chunk: sonner is only needed once
// something can be toasted, and nothing in the shell imports it statically.
const Toaster = lazy(async () => {
  const module = await import("@/ui/toast");
  return { default: module.Toaster };
});

/** The design's list per role, narrowed to what the API lets that role write (D615). */
function CreateMenu(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const go = (to: string) => (): void => {
    void navigate(to);
  };
  const salesDesk = role === "operator" || role === "branch_manager" || role === "owner";
  const items: readonly MenuItem[] = [
    ...(canEditCrmRecords(role)
      ? [{ key: "client", label: t("shell.create.client"), icon: <Users strokeWidth={1.5} />, onSelect: go("/mijozlar/yangi") }]
      : []),
    ...(salesDesk
      ? [
          { key: "deal", label: t("shell.create.deal"), icon: <SquareKanban strokeWidth={1.5} />, onSelect: go("/bitimlar?v=yangi") },
          { key: "task", label: t("shell.create.task"), icon: <SquareCheckBig strokeWidth={1.5} />, onSelect: go("/tasks?v=yangi") },
        ]
      : []),
    ...(canViewKassaBalances(role)
      ? [{ key: "payment", label: t("shell.create.payment"), icon: <Wallet strokeWidth={1.5} />, onSelect: go("/moliya?v=tolov") }]
      : []),
  ];
  return (
    <DropdownMenu
      label={t("shell.create")}
      align="end"
      items={items}
      trigger={
        <Button tone="ink" aria-label={t("shell.create")} className="px-2.5 sm:px-3">
          <Plus strokeWidth={1.5} aria-hidden="true" />
          <span className="hidden sm:inline">{t("shell.create")}</span>
        </Button>
      }
    />
  );
}

function TopBar({ onOpenPalette, onOpenDrawer }: { onOpenPalette: () => void; onOpenDrawer: () => void }): JSX.Element {
  const { t } = useTranslation();
  const agency = useAuthStore((state) => state.user?.tenant?.name);
  return (
    <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-1.5 border-b border-line bg-topbar px-3 backdrop-blur-sm lg:px-6">
      <span className="flex min-w-0 items-center gap-1.5 lg:hidden">
        <Button variant="ghost" aria-label={t("shell.drawer")} className="size-9 p-0" onClick={onOpenDrawer}>
          <Menu strokeWidth={1.5} aria-hidden="true" />
        </Button>
        <b className="truncate text-base font-semibold">{agency ?? t("app.name")}</b>
      </span>
      <span className="flex-1" />
      <button
        type="button"
        onClick={onOpenPalette}
        className="flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-control border border-line bg-surface text-sm text-ink-subtle transition-colors duration-fast ease-token hover:border-line-strong sm:w-50 sm:justify-start sm:px-2.5 lg:w-65"
      >
        <Search className="size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
        <span className="sr-only min-w-0 flex-1 truncate text-left sm:not-sr-only sm:whitespace-nowrap">{t("shell.searchHint")}</span>
        <kbd aria-hidden="true" className="hidden rounded-small border border-line bg-canvas px-1 font-mono text-xs sm:inline">⌘K</kbd>
      </button>
      <CreateMenu />
      <NotificationBell />
      <UserMenu />
    </header>
  );
}

function MobileTabBar({ onMenu }: { onMenu: () => void }): JSX.Element | null {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const counts = useNavCounts();
  if (role === undefined) return null;
  const tabs = mobileTabs(role);
  const activeTab = tabs.some((item) => isNavActive(item, pathname));
  const cell = "flex h-15 min-w-11 flex-col items-center justify-center gap-0.5 px-1";
  const label = (key: TranslationKey): JSX.Element => <span className="max-w-full truncate text-xs">{t(key)}</span>;
  return (
    <nav
      aria-label={t("nav.aria.tabs")}
      className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-line bg-tabbar pb-[env(safe-area-inset-bottom)] backdrop-blur-sm sm:hidden"
    >
      {tabs.map((item) => {
        const active = isNavActive(item, pathname);
        const Icon = item.icon;
        const count = item.countKey === undefined ? undefined : counts[item.countKey];
        return (
          <Link
            key={item.key}
            to={item.to}
            aria-current={active ? "page" : undefined}
            className={cn(cell, active ? "font-semibold text-accent-hover" : "font-medium text-ink-subtle")}
          >
            <span className="relative inline-flex">
              <Icon size={20} className={active ? "text-accent" : "text-ink-subtle"} />
              {count === undefined || count === 0 ? null : (
                <span className="absolute -top-1.5 left-3 inline-flex h-4 min-w-4 items-center justify-center rounded-pill bg-accent px-1 text-xs font-semibold text-btn-primary-ink tabular-nums outline-2 outline-surface">
                  {count}
                </span>
              )}
            </span>
            {label(item.shortKey ?? item.labelKey)}
          </Link>
        );
      })}
      <button
        type="button"
        onClick={onMenu}
        className={cn(cell, activeTab ? "font-medium text-ink-subtle" : "font-semibold text-ink")}
      >
        <Menu className="size-5" strokeWidth={1.5} aria-hidden="true" />
        {label("nav.short.menu")}
      </button>
    </nav>
  );
}

export function AppShell(): JSX.Element {
  const location = useLocation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const tenantStatus = useAuthStore((state) => state.user?.tenant?.status);
  const suspended = showsSuspendedScreen(tenantStatus, role, location.pathname);
  const locked = !suspended && locksViewport(location.pathname);
  const tabBar = !hidesTabBar(location.pathname, location.search);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // One live socket for the whole signed-in shell (D88); frames refresh whatever is open.
  useRealtimeSync();
  useApplyPreferences();

  // ⌘K and Ctrl+K open the palette from anywhere in the shell.
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key.toLowerCase() === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    }
    window.addEventListener("keydown", onKey);
    return (): void => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div className={cn("flex min-h-dvh bg-canvas text-ink", locked && "h-dvh min-h-0 overflow-hidden")}>
      <Rail />
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col sm:pb-0",
          tabBar && "pb-[calc(var(--k-tabbar-height)+env(safe-area-inset-bottom))]",
          locked && "min-h-0",
        )}
      >
        <SupportBanner />
        <ShellAlerts />
        <TopBar
          onOpenPalette={() => {
            setPaletteOpen(true);
          }}
          onOpenDrawer={() => {
            setDrawerOpen(true);
          }}
        />
        <main
          className={cn(
            "min-w-0 flex-1 px-4 pt-4 pb-12 sm:px-5 sm:pt-5 lg:px-8 lg:pt-6 lg:pb-14",
            locked && "flex min-h-0 flex-col overflow-hidden p-0 sm:p-0 lg:p-0",
          )}
        >
          {suspended ? <SuspendedScreen /> : <Outlet />}
        </main>
      </div>
      {tabBar ? (
        <MobileTabBar
          onMenu={() => {
            setDrawerOpen(true);
          }}
        />
      ) : null}
      {location.pathname === "/" ? <PushPrompt /> : null}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen} />
      <SearchPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
    </div>
  );
}
